// =========================================================================
// AskStrip — the demo's "Suggested" chip row, as a scene-level component.
//
// Port of `renderSugg(v)`'s output (demo line 4096): an eyebrow plus up to
// four question chips, picked for the view and the persona. Clicking one asks
// the assistant.
//
// Usage
// -----
//   <AskStrip view="alerts" />          // explicit demo view key
//   <AskStrip />                        // view inferred from the route
//   <AskStrip view="machine" machineId={id} />
//
// A Tier 1 scene drops it under its header. It renders nothing when the view
// has no prompts, so it is safe to place unconditionally.
//
// Which four prompts appear is `shared/assistant/suggestions.ts`; clicking is
// `shared/assistant/ask.ts`. This file is presentation only — it holds no
// state, reads no data and derives nothing.
//
// Fork-owned (zone C). No `<Commentable>` anchor: the strip is chrome inside
// somebody else's scene, and the scene owns the anchor around it.
//
// UI layer (layer 4 of the 4-layer architecture).
// =========================================================================

import { useLocation } from 'react-router-dom';
import { Sparkles } from 'lucide-react';
import { askOem } from '../shared/assistant/ask.ts';
import { suggestionsFor, type AssistantView } from '../shared/assistant/suggestions.ts';
import { useOemPersona } from '../shared/OemPersonaContext.tsx';

export interface AskStripProps {
  /**
   * The demo view key whose prompts to show. Omit to infer it from the route
   * (`suggestions.ts`'s `viewForPath`).
   */
  view?: AssistantView;
  /**
   * The machine in view, on `view="machine"`. Rewrites the three deictic
   * prompts ("this machine", "on it", "its telemetry"). Omit and it is read
   * from a `/machines/:id` path.
   */
  machineId?: string;
  /** Eyebrow text. The demo's word was "Suggested". */
  label?: string;
  className?: string;
}

/** The per-view suggested-question chips for the active persona. */
export function AskStrip({ view, machineId, label = 'Suggested', className }: AskStripProps) {
  const { pathname, search } = useLocation();
  const { personaId } = useOemPersona();
  // The machine page's `?tab=` picks the Operators / Reliability tab chips.
  const tab = new URLSearchParams(search).get('tab');
  const prompts = suggestionsFor(pathname, personaId, { view, machineId, tab });

  if (prompts.length === 0) return null;

  return (
    <div className={className}>
      <div
        className="mb-1.5 text-[10px] font-semibold uppercase tracking-[0.08em]"
        style={{ color: 'var(--ink-tertiary)' }}
      >
        {label}
      </div>
      <div className="flex flex-wrap gap-1.5">
        {prompts.map((prompt) => (
          <button
            key={prompt}
            type="button"
            onClick={() => askOem(prompt)}
            title="Ask the assistant"
            className="group inline-flex items-center gap-1.5 rounded-full border border-[var(--glass-border)] bg-[var(--glass-surface-soft)] px-3 py-1.5 text-left text-[12.5px] text-zinc-600 transition-all duration-150 hover:-translate-y-px hover:border-[var(--glass-border-strong)] hover:bg-[var(--glass-surface)] hover:text-zinc-900 hover:shadow-sm active:translate-y-0 dark:text-zinc-300 dark:hover:text-zinc-100"
          >
            <Sparkles
              className="h-3 w-3 flex-shrink-0 text-zinc-400 transition-colors group-hover:text-accent-500"
              aria-hidden
            />
            <span>{prompt}</span>
          </button>
        ))}
      </div>
    </div>
  );
}
